/* eslint-disable @atlaskit/design-system/no-html-anchor */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx, keyframes } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SectionName } from '../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../common/analytics/useSectionReveal';
import { CONNECTOR_ICONS } from '../../../icons';
import { SpacingWrapper } from '../../SpacingWrapper';

import { messages } from './messages';
import { spiralFromCenter } from './utils';

// Rotation angles in degrees for each connector icon to create the tilted effect from the design
const CONNECTOR_ROTATIONS = [0, -8, 5, -3, 10, -6, 2, -10, 7, -4, 8, -5, 3, -7, 6];

// Duration in seconds for one full cycle of the marquee
const MARQUEE_DURATION_SECONDS = 60;

const scrollMarquee = keyframes({
	'0%': {
		transform: 'translateX(-50%)',
	},
	'100%': {
		transform: 'translateX(0)',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.500'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		height: '800px',
		'@media (max-width: 30rem)': {
			height: 'auto',
			paddingBlock: token('space.1000'),
		},
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	titleWrapper: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.300'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},
	eyebrow: {
		margin: 0,
		font: token('font.body'),
		color: token('color.border.input'),
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.semibold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '24px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		textAlign: 'center',
	},
	title: {
		margin: 0,
		font: token('font.heading.xxlarge'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#F0F1F2',
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '48px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		fontVariantNumeric: 'tabular-nums lining-nums',
		textAlign: 'center',
	},
	buttonWrapper: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.0'),
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#292A2E',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition:
			'background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			borderColor: '#44474C',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.12)',
			transform: 'scale(0.98)',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	connectorRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		paddingTop: token('space.600'),
		paddingBottom: token('space.600'),
		width: '100%',
		overflow: 'hidden',
	},
	marqueeTrack: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		width: 'fit-content',
		animationName: scrollMarquee,
		animationTimingFunction: 'linear',
		animationIterationCount: 'infinite',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		animationDuration: `${MARQUEE_DURATION_SECONDS}s` as const,
	},
	connectorIcon: {
		width: '160px',
		height: '160px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '40px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		marginLeft: token('space.negative.300'),
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		overflow: 'hidden',
		'@media (max-width: 30rem)': {
			width: '100px',
			height: '100px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
			borderRadius: '20px',
		},
	},
	connectorIconFirst: {
		marginLeft: 0,
	},
	connectorIconImage: {
		width: '100%',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		userSelect: 'none' as const,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		pointerEvents: 'none' as const,
	},
});

// spiralFromCenter can produce sparse arrays when the input length is even,
// so we filter out any empty slots before duplicating for the marquee.
const sortedConnectors = spiralFromCenter(CONNECTOR_ICONS).filter(Boolean);

// Duplicate the list for seamless infinite scrolling — the second copy
// ensures continuity when the animation resets from translateX(-50%) to 0.
const marqueeConnectors = [...sortedConnectors, ...sortedConnectors];

export const ConnectorShowcaseSection = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.ConnectorShowcase);

	return (
		<div ref={sectionRef} css={styles.container}>
			<SpacingWrapper>
				<Box xcss={styles.titleWrapper}>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={styles.eyebrow}>
						<FormattedMessage {...messages.connectorShowcaseEyebrow} />
					</p>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/design-system/use-heading */}
					<h2 css={styles.title}>
						<FormattedMessage {...messages.connectorShowcaseTitle} />
					</h2>
				</Box>
			</SpacingWrapper>
			<div css={styles.connectorRow} aria-hidden="true">
				<div css={styles.marqueeTrack}>
					{marqueeConnectors.map((product, i) => {
						const rotation = CONNECTOR_ROTATIONS[i % CONNECTOR_ROTATIONS.length];
						return (
							<div
								key={`${product.name}-${i}`}
								css={[styles.connectorIcon, i === 0 && styles.connectorIconFirst]}
								style={{ transform: `rotate(${rotation}deg)` }}
							>
								<img css={styles.connectorIconImage} src={product.svg} alt="" role="presentation" />
							</div>
						);
					})}
				</div>
			</div>
			<SpacingWrapper>
				<Box xcss={styles.buttonWrapper}>
					<a
						aria-label={formatMessage(messages.browseConnectorsAriaLabel)}
						css={styles.button}
						href="https://www.atlassian.com/software/rovo/connectors"
					>
						<FormattedMessage {...messages.browseConnectorsButton} />
						<ArrowUpRightIcon label="" color="currentColor" />
					</a>
				</Box>
			</SpacingWrapper>
		</div>
	);
};
